<template>
  <div>
    <!-- Date Picker Trigger -->
    <div 
      id="datePickerDiv" 
      class="w-100 d-flex justify-content-center align-items-start p-3 text-dark"
      @click="(event) => handleToggleDatePicker(event)">
      <div class="w-100 d-flex justify-content-start align-items-center">
        <div class="me-2 align-self-center">
          <svg class="icon tf-icon-22 text-blue-900">
            <use xlink:href="#___icon___calander"></use>
          </svg>
        </div>
        <div class="datepicker-label">
          <label class="input-title datepicker-input-label">
            {{ dateRangeLabel }}
          </label>
        </div>
      </div>
    </div>

    <!-- Desktop DatePicker -->
    <div 
      class="datePickerContainerRef datepicker-input position-absolute" 
      ref="datePickerContainerRef"
      :class="[isOpened ? 'd-block' : 'd-none']"
      @click.stop>
      <VDatePicker 
        v-if="!isMobile" 
        v-model="selectedDate" 
        :key="`datepicker-${datePickerKey}`"
        :locale="locale" 
        ref="datePickerRef" 
        :columns="2" 
        :rows="1"
        :first-day-of-week="2"
        :color="selectedColor" 
        :attributes="dateAttributes" 
        :available-dates="availableDates" 
        :min-date="minSelectableDate"
        :max-date="maxSelectableDate"
        mode="date"
        :disabled-dates="disabledDates" 
        @dayclick="handleDateSelection" 
        @update:pages="handlePagesChange" 
        :step="1">
        <template #footer>
          <div class="d-flex justify-content-around align-items-center m-2 border-top">
            <label class="input-title fw-bold m-2">
              {{ dateRangeLabel }}
            </label>
          </div>
        </template>
      </VDatePicker>
    </div>

    <!-- Mobile Custom Popup -->
    <div 
      v-if="isMobile && isOpened" 
      class="mobile-datepicker-popup" 
      @click.stop="handleMobilePopupClick">
      <div
        class="mobile-datepicker-content bg-white rounded-4 w-100 h-100 overflow-auto position-relative d-flex flex-column align-items-center"
        @click.stop>
        <!-- Sticky Header -->
        <div class="datepicker-header-sticky">
          <div class="d-flex justify-content-end w-100 p-3">
            <ClsBtn @click="closeDatePicker" />
          </div>
        </div>

        <!-- Scrollable Calendar Content -->
        <div class="datepicker-calendar-content">
          <VDatePicker 
            v-model="selectedDate" 
            :key="`datepicker-mobile-${datePickerKey}`"
            :locale="locale" 
            ref="datePickerRef" 
            :columns="1" 
            :rows="6"
            :first-day-of-week="2"
            :color="selectedColor" 
            :attributes="dateAttributes" 
            :available-dates="availableDates" 
            :min-date="minSelectableDate"
            :max-date="maxSelectableDate"
            mode="date"
            :disabled-dates="disabledDates" 
            @dayclick="handleDateSelection" 
            @update:pages="handlePagesChange">
          </VDatePicker>
        </div>

        <!-- Sticky Footer -->
        <div class="datepicker-footer-sticky">
          <div class="d-flex justify-content-around align-items-center p-3 border-top bg-white">
            <label class="input-title fw-bold m-0">
              {{ dateRangeLabel }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { DatePicker as VDatePicker } from "v-calendar";
import { onClickOutside } from "@vueuse/core";
import { useStore } from 'vuex';
import { format } from 'date-fns';
import * as _ from "lodash";

// Local imports
import {
  parseDateString,
  formatDateToString,
  formatDateRange,
} from "@/vue/planning/utils/datePickerHelpers";
import { AttributesRef, DateInfo, RangeType } from "@/types/datePicker";
import {
  getLanguageCode,
  getAdjacentMonth,
  parseHashParams,
  getSasonBasedOnDate,
  formatDateForHash,
  isMobile as checkMobile,
} from "@/utils/utils";
import { HashParams } from "@/types";
import { airportCodeMap } from "@/config/airportCodeMap";
import ClsBtn from '@/vue/common/clsBtn.vue';

// ============================================================================
// Types & Interfaces
// ============================================================================

interface Props {
  show?: boolean;
}

interface Emit {
  (e: "close"): void;
}

interface DatePickerPages {
  month: number;
  year: number;
}

interface SearchParams {
  locale: string;
  packCode: string;
  liheId: string;
  month?: string;
  origin?: string;
  startDate?: string;
  season?: string;
}

// ============================================================================
// Props & Emits
// ============================================================================

const props = withDefaults(defineProps<Props>(), {
  show: false,
});

const emit = defineEmits<Emit>();

// ============================================================================
// Store & Initialization
// ============================================================================

const store = useStore();

// ============================================================================
// Constants
// ============================================================================

const SELECTED_COLOR = "blue";
const DATE_FETCH_DEBOUNCE_MS = 500;
const MONTHS_TO_FETCH_COUNT = 5; // 2 before, current, 2 after
const DATE_RANGE_YEARS = 2; // Years before/after current year for disabled dates

// ============================================================================
// Reactive State - Date Management
// ============================================================================

const startDate = ref<string | null>(store.state.dates["BeginDate"]);
const endDate = ref<string | null>(store.state.dates["EndDate"]);
const selectedDate = ref<string | null>(startDate.value);
const prevSelectedDate = ref<Date | null>(null);
const tempPrevSelectedDate = ref<Date | null>(null);
const availableDates = ref<Date[]>([]);
const currentMonth = ref<string>("");
const currentYear = ref<string>("");
const dateRange = ref<RangeType>({ start: "", end: "" });
const originalStartDate = ref<string | null>(null);
const originalEndDate = ref<string | null>(null);
const datePickerKey = ref<number>(0);

// ============================================================================
// Reactive State - UI State
// ============================================================================

const isMobile = ref<boolean>(false);
const isOpened = ref<boolean>(props.show);
const isSelectingEndDate = ref<boolean>(false);
const isFetchingDates = ref<boolean>(false);
const isClosing = ref<boolean>(false);
const justCompletedSelection = ref<boolean>(false);

// ============================================================================
// Reactive State - Refs
// ============================================================================

const datePickerContainerRef = ref<HTMLElement | null>(null);
const datePickerRef = ref<InstanceType<typeof VDatePicker> | null>(null);

// ============================================================================
// Reactive State - Configuration
// ============================================================================

const locale = ref(getLanguageCode(store.getters.locale));
const selectedColor = ref(SELECTED_COLOR);

const dateAttributes = ref<AttributesRef[]>([
  {
    key: "selected-dates",
    highlight: SELECTED_COLOR,
    dates: { start: new Date(), end: new Date() },
  },
]);

// ============================================================================
// Reactive State - API Parameters
// ============================================================================

const searchParams = ref<SearchParams>({
  locale: store.getters.locale,
  packCode: store.state.packCode || '',
  liheId: window.lihe.id,
});

const seasonParams = ref({
  season: "",
});

const monthParams = ref<Record<string, string>>({
  month: "",
});

// ============================================================================
// Computed Properties
// ============================================================================

const content = computed(() => store.state.content);

const flightOrigin = computed<string>(() => {
  const origin = store.state.flightOrigin;
  const noneAirport = airportCodeMap.get("None")?.name;
  
  if (origin && origin !== noneAirport) {
    return origin;
  }
  return "";
});

const departureText = computed((): string => {
  if (!startDate.value) return "";
  return formatDateRange(store.getters.locale, startDate.value);
});

const returnText = computed((): string => {
  if (!endDate.value) return "";
  return formatDateRange(store.getters.locale, endDate.value);
});

const dateRangeLabel = computed((): string => {
  if (startDate.value && endDate.value && departureText.value && returnText.value) {
    return `${departureText.value} - ${returnText.value}`;
  }
  return content.value.planner_datePicker_depart_return;
});

const disabledDates = computed(() => generateDisabledDates(availableDates.value));

// ============================================================================
// Methods - Date Selection & Search
// ============================================================================

/** Today at midnight – used as min-date so past dates are disabled and past months cannot be navigated to */
const minSelectableDate = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
});

/** Today at midnight – used as max-date so future dates up to 18 months from today are disabled and future months cannot be navigated to */
const maxSelectableDate = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setMonth(d.getMonth() + 18); // 18 months from today
  return d;
});

/**
 * Handles the complete date range selection and triggers search
 */
const handleSearch = async (departureDate: Date, returnDate: Date): Promise<void> => {
  if (!(departureDate instanceof Date) || !(returnDate instanceof Date)) {
    console.error('handleSearch: Invalid date parameters');
    return;
  }

  // Format new dates
  const newStartDate = formatDateToString(departureDate);
  const newEndDate = formatDateToString(returnDate);
  
  // Get current dates from store before updating
  const currentStartDate = store.state.dates["BeginDate"];
  const currentEndDate = store.state.dates["EndDate"];
  
  // Check if dates have actually changed
  const datesChanged = newStartDate !== currentStartDate || newEndDate !== currentEndDate;

  // Update local state
  startDate.value = newStartDate;
  endDate.value = newEndDate;
  
  // Update store
  store.dispatch("setDates", {
    BeginDate: startDate.value,
    EndDate: endDate.value,
  });

  // Reset selection mode
  isSelectingEndDate.value = false;

  // Update range and attributes
  dateRange.value = {
    start: startDate.value!,
    end: endDate.value!,
  };

  dateAttributes.value[0].dates = {
    start: departureDate,
    end: returnDate,
  };

  // Only reset WiypCall if dates have actually changed
  if (datesChanged) {
    store.dispatch("resetWiypCall");
  }
  // Close picker first (skip reset since we've already handled the state)
  // The justCompletedSelection flag should already be set by completeEndDateSelection
  await toggleDatePicker(false, true);
  
  // Wait for DOM to update
  await nextTick();
  
  // Refresh available dates for current month
  const month = departureDate.getMonth() + 1;
  await fetchStartDates(month.toString().padStart(2, "0"));
  
  // Reset the flag after a delay to allow click events to settle
  setTimeout(() => {
    justCompletedSelection.value = false;
  }, 300);
};

/**
 * Handles date selection from the calendar
 */
const handleDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  if (dateInfo.isDisabled) return;

  // Handle season switching if needed
  await handleSeasonSwitch(dateInfo);

  // If selecting end date, complete the selection
  if (isSelectingEndDate.value) {
    prevSelectedDate.value = tempPrevSelectedDate.value;
    await completeEndDateSelection(dateInfo);
    return;
  }

  // Otherwise, start selecting departure date
  await startDepartureDateSelection(dateInfo);
};

/**
 * Handles season switching based on selected date
 */
const handleSeasonSwitch = async (dateInfo: DateInfo): Promise<void> => {
  const formattedDate = formatDateForHash(dateInfo.id, true);
  const seasonBasedOnDate = getSasonBasedOnDate(formattedDate);

  if (seasonBasedOnDate !== store.state.season) {
    store.commit("setSeason", seasonBasedOnDate);
    window.seasonManager.switchSeasonWithoutReloading();
    seasonParams.value.season = seasonBasedOnDate;
  }
};

/**
 * Completes the end date selection
 */
const completeEndDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  const returnDate = parseDateString(dateInfo.id);
  
  if (!prevSelectedDate.value || !returnDate) {
    return;
  }

  // Set flag before search to prevent any click events from reopening
  justCompletedSelection.value = true;
  
  await handleSearch(prevSelectedDate.value, returnDate);
};

/**
 * Starts the departure date selection process
 */
const startDepartureDateSelection = async (dateInfo: DateInfo): Promise<void> => {
  // Enter end date selection mode
  isSelectingEndDate.value = true;

  // Parse and store the departure date
  const departureDate = parseDateString(dateInfo.id);
  if (!departureDate) return;
  
  //prevSelectedDate.value = departureDate;
  tempPrevSelectedDate.value = departureDate;
  selectedDate.value = dateInfo.id;

  // Prepare parameters for fetching end dates
  monthParams.value.month = extractMonthFromDateId(dateInfo.id);

  if (flightOrigin.value && store.state.transportationSelected) {
    monthParams.value.origin = flightOrigin.value;
  }

  // Fetch available end dates
  try {
    const endDates = await window.searchEngineService.getRelatedPackages(
      {
        ...searchParams.value,
        ...monthParams.value,
        startDate: dateInfo.id,
      },
      "getEndDates"
    );

    // Jump to first available end date if not in view
    if (endDates[0] && !isDateInView(endDates[0])) {
      await jumpToDate(new Date(endDates[0]));
    }

    // Update available dates and clear attributes
    dateAttributes.value[0].dates = null;
    availableDates.value = formatDatesArray(endDates);
  } catch (error) {
    console.error("Error fetching end dates:", error);
  }
};

// ============================================================================
// Methods - Date Picker UI Control
// ============================================================================

/**
 * Toggles the date picker visibility
 */
const handleToggleDatePicker = (event?: Event): void => {
  // Stop event propagation if it came from calendar
  if (event) {
    const target = event.target as HTMLElement;
    const calendarContainer = document.querySelector(".datePickerContainerRef");
    const mobileContainer = document.querySelector(".mobile-datepicker-popup");
    
    // If click came from within the calendar container, stop it
    if (calendarContainer?.contains(target) || mobileContainer?.contains(target)) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
  }
  
  // If we just completed a selection, block any toggle for a short period
  if (justCompletedSelection.value) {
    return;
  }
  
  const shouldShow = isMobile.value ? false : !isOpened.value;
  
  // Only prevent closing if we're in the process of closing
  if (!shouldShow && isClosing.value) return;
  
  toggleDatePicker(shouldShow);
};

/**
 * Opens or closes the date picker
 */
const toggleDatePicker = async (show?: boolean, skipReset = false): Promise<void> => {
  const pickerDiv = document.getElementById("datePickerDiv") as HTMLElement | null;

  // Determine if we should show or hide
  if (typeof show === "undefined") {
    show = isMobile.value ? false : !isOpened.value;
  }

  // Prevent closing if we're already in the process of closing (unless skipReset is true)
  if (!show && isClosing.value && !skipReset) {
    return;
  }

  if (show) {
    // Reset flags when opening
    isClosing.value = false;
    // Don't reset justCompletedSelection here - let the timeout handle it
    
    // For mobile, set isOpened first so the popup renders, then open
    if (isMobile.value) {
      isOpened.value = true;
      await nextTick(); // Wait for mobile popup to render
    }
    
    await openDatePicker(pickerDiv);
  } else {
    // Set closing flag to prevent immediate reopening
    isClosing.value = true;
    closeDatePicker(pickerDiv, skipReset);
    // Reset closing flag after a short delay
    setTimeout(() => {
      isClosing.value = false;
    }, 100);
  }

  // Set isOpened for desktop (mobile already set above)
  if (!isMobile.value || !show) {
    isOpened.value = show;
  }
};

/**
 * Opens the date picker
 */
const openDatePicker = async (pickerDiv: HTMLElement | null): Promise<void> => {
  if (isMobile.value) {
    document.body.style.overflow = 'hidden';
  }

  // For mobile, wait for popup to be fully rendered
  if (isMobile.value) {
    await nextTick();
    // Additional wait to ensure VDatePicker is mounted
    await nextTick();
  }

  if (!pickerDiv || (!isMobile.value && !datePickerContainerRef.value)) return;

  // Save original dates from store before any new selection
  originalStartDate.value = store.state.dates["BeginDate"];
  originalEndDate.value = store.state.dates["EndDate"];

  // Ensure local state is in sync with store (in case watchers haven't updated yet)
  startDate.value = originalStartDate.value;
  endDate.value = originalEndDate.value;

  // Reset selection state when opening
  isSelectingEndDate.value = false;
  prevSelectedDate.value = null;
  tempPrevSelectedDate.value = null;
  
  // Reset selectedDate to original start date - this ensures VDatePicker shows correct date
  selectedDate.value = originalStartDate.value;
  
  // Wait for next tick to ensure VDatePicker updates
  await nextTick();

  // Extract month and year from startDate to ensure calendar shows correct period
  if (originalStartDate.value) {
    const beginDateParts = originalStartDate.value.split("/");
    const monthFromBeginDate = beginDateParts[1];
    const yearFromBeginDate = beginDateParts[2];
    
    // Update current month and year
    currentMonth.value = monthFromBeginDate;
    currentYear.value = yearFromBeginDate;
    
    // Fetch start dates for the correct period
    await fetchStartDates(currentMonth.value);
    
    // Move calendar to the correct period (especially important for mobile)
    // Wait one more tick for mobile to ensure calendar is ready
    if (isMobile.value) {
      await nextTick();
    }
    await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  } else if (currentMonth.value && currentYear.value) {
    // Fallback: use existing currentMonth/currentYear if startDate is not available
    await fetchStartDates(currentMonth.value);
  }

  // Reset date attributes to show current range (use original dates)
  if (originalStartDate.value && originalEndDate.value) {
    dateAttributes.value[0].dates = {
      start: parseDateString(originalStartDate.value)!,
      end: parseDateString(originalEndDate.value)!,
    };
  } else {
    dateAttributes.value[0].dates = null;
  }

  setTimeout(() => {
    document.addEventListener("click", handleOutsideClick);
    if (pickerDiv) {
      pickerDiv.style.color = "var(--primary-blue)";
    }
  }, 1);
};

/**
 * Closes the date picker
 */
const closeDatePicker = (pickerDiv?: HTMLElement | null, skipReset = false): void => {
  const pickerDivElement = pickerDiv || document.getElementById("datePickerDiv") as HTMLElement | null;

  // If user selected a start date but didn't select an end date, restore original dates
  // Check both prevSelectedDate and tempPrevSelectedDate since tempPrevSelectedDate is set when selecting start date
  if (!skipReset && isSelectingEndDate.value && (prevSelectedDate.value || tempPrevSelectedDate.value)) {
    // Reset selection state first
    isSelectingEndDate.value = false;
    prevSelectedDate.value = null;
    tempPrevSelectedDate.value = null;
    
    // Restore original dates from when picker was opened
    startDate.value = originalStartDate.value;
    endDate.value = originalEndDate.value;
    
    // Update store with original dates
    store.dispatch("setDates", {
      BeginDate: originalStartDate.value,
      EndDate: originalEndDate.value,
    });
    
    // Update dateRange to reflect original dates
    dateRange.value = {
      start: originalStartDate.value || "",
      end: originalEndDate.value || "",
    };
    
    // IMPORTANT: Set selectedDate FIRST to ensure VDatePicker shows correct date
    // This must happen before updating date attributes
    selectedDate.value = originalStartDate.value;
    
    // Increment key to force VDatePicker re-render with correct date
    datePickerKey.value += 1;
    
    // Clear date attributes first to remove visual selection from orphan date
    dateAttributes.value[0].dates = null;
    
    // Then set date attributes if we have a complete date range
    if (originalStartDate.value && originalEndDate.value) {
      dateAttributes.value[0].dates = {
        start: parseDateString(originalStartDate.value)!,
        end: parseDateString(originalEndDate.value)!,
      };
    }
  } else if (!skipReset && isSelectingEndDate.value) {
    // Reset selection state even if prevSelectedDate is null
    // Also reset tempPrevSelectedDate to ensure clean state
    tempPrevSelectedDate.value = null;
    
    // Reset selectedDate to original start date
    selectedDate.value = originalStartDate.value;
    
    // Increment key to force VDatePicker re-render with correct date
    datePickerKey.value += 1;
    
    resetDateSelection();
  }

  // Restore body overflow on mobile
  if (isMobile.value) {
    document.body.style.overflow = 'auto';
  }

  if (!pickerDivElement || !datePickerContainerRef.value) return;

  document.removeEventListener("click", handleOutsideClick);
  pickerDivElement.style.color = "black";
  emit("close");
};

/**
 * Resets the date selection state
 */
const resetDateSelection = async (): Promise<void> => {
  isSelectingEndDate.value = false;
  selectedDate.value = startDate.value;
  prevSelectedDate.value = null;
  tempPrevSelectedDate.value = null;

  // Reset available dates to show start dates
  if (currentMonth.value && currentYear.value) {
    await fetchStartDates(currentMonth.value);
  }

  // Reset date attributes
  if (startDate.value && endDate.value) {
    dateAttributes.value[0].dates = {
      start: parseDateString(startDate.value)!,
      end: parseDateString(endDate.value)!,
    };
  } else {
    dateAttributes.value[0].dates = null;
  }
};

/**
 * Handles clicks outside the date picker
 */
const handleOutsideClick = (event: MouseEvent): void => {
  const isDatePickerContainer = document.querySelector(".datePickerContainerRef")?.contains(event.target as Node);
  const isShowDatePickerBtn = document.querySelector(".showDatePickerBtn")?.contains(event.target as Node);
  
  if (event.target && !isDatePickerContainer && !isShowDatePickerBtn) {
    toggleDatePicker(false);
  }
};

/**
 * Handles mobile popup backdrop clicks
 */
const handleMobilePopupClick = (event: MouseEvent): void => {
  if (event.target === event.currentTarget) {
    toggleDatePicker(false);
  }
};

// ============================================================================
// Methods - Date Fetching & Management
// ============================================================================

/**
 * Fetches start dates for the specified month and surrounding months
 */
const fetchStartDates = async (
  monthToFetch: string,
): Promise<void> => {
  if (!currentYear.value || !monthToFetch) return;

  const monthsToFetch = getMonthsToFetch(monthToFetch);

  try {
    const startDatesPromises = monthsToFetch.map((month) => {
      monthParams.value.month = `${currentYear.value}-${month}`;

      if (flightOrigin.value && store.state.transportationSelected) {
        monthParams.value.origin = flightOrigin.value;
      }

      return window.searchEngineService.getStartDates({
        ...searchParams.value,
        ...monthParams.value,
      });
    });

    const resolvedStartDates = await Promise.all(startDatesPromises);
    const allStartDates = resolvedStartDates.flatMap(formatDatesArray);

    // Update available dates
    availableDates.value = [...new Set(allStartDates)];
    // availableDates.value = [...new Set([...availableDates.value, ...allStartDates])];

    // Remove duplicates from availableDates.value (in-place, keeping only unique dates by time value)
    availableDates.value = Array.from(
      new Map(
        availableDates.value.map(date => [date.getTime(), date])
      ).values()
    );
  } catch (error) {
    console.error("Error fetching start dates:", error);
  }
};

/**
 * Gets the list of months to fetch (2 before, current, 2 after)
 */
const getMonthsToFetch = (centerMonth: string): string[] => {
  const months: string[] = [];
  
  // Get 2 months before
  let month = centerMonth;
  for (let i = 0; i < 2; i++) {
    month = getAdjacentMonth(month, "prev");
    months.unshift(month);
  }
  
  // Add center month
  months.push(centerMonth);
  
  // Get 2 months after
  month = centerMonth;
  for (let i = 0; i < 2; i++) {
    month = getAdjacentMonth(month, "next");
    months.push(month);
  }
  
  return months;
};

/**
 * Handles calendar page changes
 */
const handlePagesChange = async (pages: DatePickerPages[]): Promise<void> => {
  const firstPage = pages[0];
  const formattedMonth = firstPage.month.toString().padStart(2, "0");
  const formattedYear = firstPage.year.toString();

  const isSameMonthYear = 
    currentMonth.value === formattedMonth && 
    currentYear.value === formattedYear;

  if (isSameMonthYear || isFetchingDates.value || isSelectingEndDate.value) {
    return;
  }

  currentMonth.value = formattedMonth;
  currentYear.value = formattedYear;

  // Debounce fetching
  isFetchingDates.value = true;
  setTimeout(() => {
    isFetchingDates.value = false;
  }, DATE_FETCH_DEBOUNCE_MS);

  await fetchStartDates(currentMonth.value);
};

/**
 * Formats an array of date strings to Date objects
 */
const formatDatesArray = (dateStrings: string[]): Date[] => {
  return dateStrings
    .map((dateString) => parseDateString(dateString))
    .filter((date): date is Date => date !== null);
};

/**
 * Generates disabled dates based on available dates
 */
const generateDisabledDates = (available: Date[]): Date[] => {
  const result: Date[] = [];
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  startDate.setFullYear(today.getFullYear() - DATE_RANGE_YEARS);
  endDate.setFullYear(today.getFullYear() + DATE_RANGE_YEARS);

  const availableSet = new Set(
    available.map(date => format(date, 'yyyy-MM-dd'))
  );

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    const dayStr = format(d, 'yyyy-MM-dd');
    if (!availableSet.has(dayStr)) {
      result.push(new Date(d));
    }
  }

  return result;
};

/**
 * Checks if a date is currently visible in the calendar view
 */
const isDateInView = (dateString: string): boolean => {
  const selector = `${isMobile.value ? '.mobile-datepicker-popup' : ''} [class*="vc-day id-${dateString}"].in-month`;
  const dateElement = document.querySelector(selector);
  return dateElement !== null;
};

/**
 * Jumps the calendar to a specific date
 */
const jumpToDate = async (targetDate: Date): Promise<void> => {
  if (!datePickerRef.value) return;

  await datePickerRef.value.move({
    month: targetDate.getMonth() + 1,
    year: targetDate.getFullYear(),
  });
};

/**
 * Moves the calendar to a specific month and year
 */
const moveDatePickerMonth = async (month: string, year: string): Promise<void> => {
  await datePickerRef.value?.move({
    month: +month,
    year: +year,
  });
};

/**
 * Extracts month string from date ID (YYYY-MM-DD format)
 */
const extractMonthFromDateId = (dateId: string): string => {
  return dateId.split("-").slice(0, 2).join("-");
};

/**
 * Initializes the date picker with existing data
 */
const initializeDatePicker = async (): Promise<void> => {
  if (!startDate.value || !endDate.value || !dateAttributes.value[0].dates) {
    return;
  }

  // Set initial date attributes
  dateAttributes.value[0].dates.start = parseDateString(startDate.value)!;
  dateAttributes.value[0].dates.end = parseDateString(endDate.value)!;

  // Set initial range
  dateRange.value = {
    start: store.state.dates["BeginDate"],
    end: store.state.dates["EndDate"],
  };

  // Extract month and year from begin date
  const beginDateParts = store.state.dates["BeginDate"].split("/");
  const monthFromBeginDate = beginDateParts[1];
  const yearFromBeginDate = beginDateParts[2];

  currentMonth.value = monthFromBeginDate;
  currentYear.value = yearFromBeginDate;

  // Fetch start dates and move calendar
  await fetchStartDates(currentMonth.value);
  await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
};

// ============================================================================
// Lifecycle Hooks
// ============================================================================

onMounted(() => {
  isMobile.value = checkMobile();
  
  if (!isMobile.value) {
    onClickOutside(datePickerContainerRef, () => {
      if (!isClosing.value) {
        toggleDatePicker(false);
      }
    });
  }
});

// ============================================================================
// Watchers
// ============================================================================

// Watch for prop changes
watch(() => props.show, (newValue) => {
  // Allow opening even if closing, but prevent closing if already closing
  if (newValue || !isClosing.value) {
    toggleDatePicker(newValue);
  }
});

// Watch for packCode changes
watch(
  () => store.state.packCode,
  async (newPackCode) => {
    const hashParams = parseHashParams() as HashParams;

    searchParams.value.packCode = newPackCode;
    seasonParams.value.season = hashParams.season;

    toggleDatePicker(false);

    const beginDateParts = store.state.dates["BeginDate"].split("/");
    const monthFromBeginDate = beginDateParts[1];
    const yearFromBeginDate = beginDateParts[2];

    await fetchStartDates(currentMonth.value);
    await moveDatePickerMonth(monthFromBeginDate, yearFromBeginDate);
  }
);

// Watch for participants changes
watch(
  () => store.state.participants,
  () => {
    toggleDatePicker(false);
  }
);

// Watch for BeginDate changes
watch(
  () => store.state.dates["BeginDate"],
  (newBeginDate) => {
    startDate.value = newBeginDate;
    // Update dateRange to keep it in sync
    if (newBeginDate && endDate.value) {
      dateRange.value = {
        start: newBeginDate,
        end: endDate.value,
      };
    }
  }
);

// Watch for EndDate changes
watch(
  () => store.state.dates["EndDate"],
  (newEndDate) => {
    endDate.value = newEndDate;
    // Update dateRange to keep it in sync
    if (startDate.value && newEndDate) {
      dateRange.value = {
        start: startDate.value,
        end: newEndDate,
      };
    }
  }
);

// Watch for loading state to initialize
watch(
  () => store.state.loading,
  () => {
    initializeDatePicker();
  }
);

</script>

<style scoped>
.datepicker-input {
  z-index: 99;
  overflow: visible !important;
}

.datePickerContainerRef {
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  z-index: 1000000;
}

.date-picker-root {
  position: relative;
}

.datepicker-input-label {
  font-size: 16px;
  cursor: pointer;
}

/* General calendar days */
:deep(.vc-day-content) {
  font-weight: 500 !important;
  width: 38px !important;
  height: 38px !important;
  font-size: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

:deep(.vc-highlight-content-solid) {
  background: var(--primary-blue);
  color: white !important;
}

:deep(.vc-highlight-content-solid:hover) {
  background-color: var(--primary-blue) !important;
}

/* Disabled day override */
:deep(.vc-day.disabled:not(.vc-highlight-content-solid):not(.vc-highlight-content-outline)),
:deep(.vc-day-content.disabled:not(.vc-highlight-content-solid):not(.vc-highlight-content-outline)) {
  pointer-events: none;
  color: var(--grey-400) !important;
  cursor: pointer !important;
  background-color: transparent !important;
}

:deep(.vc-highlight-content-outline) {
  border: none !important;
  outline: none !important;
  background: var(--primary-blue) !important;
  color: white !important;
}

:deep(.vc-highlight) {
  background-color: var(--blue-100) !important;
  height: 38px !important;
}

:deep(.vc-day:has(div.vc-highlights):not(:has(.vc-highlight-content-solid, .vc-highlight-content-outline)) .vc-day-content) {
  color: var(--primary-blue);
}

:deep(.vc-header) {
  background-color: transparent !important;
}

:deep(.vc-title) {
  background-color: transparent !important;
}

:deep(.vc-arrow) {
  background-color: transparent !important;
}

:deep(.vc-container [role=button], .vc-container button) {
  cursor: pointer;
}

:deep(.vc-pane-layout) {
  padding: 10px 30px;
  gap: 30px;
}

:deep(.vc-title span) {
  font-size: 14px !important;
}

:deep(.vc-title-wrapper button) {
  color: var(--grey-grey-900, #0D141B);
  text-align: center;
  font-size: inherit !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  background-color: transparent;
}

:deep(.vc-header is-lg) {
  margin-top: 0px !important;
}

:deep(.vc-weekday) {
  font-weight: 400 !important;
  font-size: inherit !important;
}

:deep(.vc-weeks) {
  margin-top: 15px;
}

/* Days buttons */
:deep(.vc-day) {
  margin-bottom: 5px;
}

:deep(.vc-day:not(.disabled)) {
  font-size: 18px;
  font-weight: 800;
}

/* Mobile Custom Popup Styles */
.mobile-datepicker-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100001 !important;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Sticky Header and Footer for Mobile */
.datepicker-header-sticky {
  position: sticky;
  top: 0;
  z-index: 10;
  background: white;
  width: 100%;
  border-bottom: 1px solid #e9ecef;
}

.datepicker-footer-sticky {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: white;
  width: 100%;
  border-top: 1px solid #e9ecef;
}

.datepicker-calendar-content {
  flex: 1;
  width: 100%;
  overflow-y: auto;
  padding: 0 1rem;
}

.mobile-close-btn {
  transition: background-color 0.2s;
}

.mobile-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.mobile-close-btn svg {
  fill: #666;
}

/* Mobile-specific calendar adjustments */
@media (max-width: 768px) {
  .mobile-datepicker-content {
    padding: 0;
  }

  .datepicker-calendar-content {
    padding: 1rem;
  }

  :deep(.vc-pane-layout) {
    padding: 5px 15px;
    gap: 15px;
    border: none;
  }

  :deep(.vc-day-content) {
    width: 35px !important;
    height: 35px !important;
    font-size: 16px;
  }

  :deep(.vc-highlight) {
    height: 35px !important;
  }

  :deep(.vc-container) {
    border: none;
    width: 100%;
  }
}
</style>
